import { GraduationCap, History, Play, RotateCcw } from 'lucide-react';
import type { TrainingAttemptSummary, TrainingRetryMode } from '@hcc/types';
import { Button } from '../../ui/Button';
import { Pill } from '../../ui/Pill';

/**
 * The entry screen for a module: pass mark, size, any previous attempts, and
 * the Start / Resume call to action. When a saved checkpoint exists it says
 * how far the learner got and when it saved, so "pick up where you left off"
 * is explicit rather than a silent jump into the middle of the deck.
 */
export function ModuleOverview({
  graded = true,
  passMark,
  totalQuestions,
  retryMode,
  attempts,
  bestScore,
  resume,
  onBegin,
}: {
  /** False = an assessment: no pass mark, no scores in the history. */
  graded?: boolean;
  passMark: number;
  totalQuestions: number;
  retryMode: TrainingRetryMode;
  attempts: TrainingAttemptSummary[];
  bestScore: number | null;
  /** Saved-progress summary, or null for a fresh start. */
  resume: { answered: number; updatedAt: string | null } | null;
  onBegin: () => void;
}) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {graded ? (
          <Pill tone="info" size="sm">
            Pass mark {passMark}%
          </Pill>
        ) : (
          <Pill tone="info" size="sm">
            Not marked
          </Pill>
        )}
        <Pill tone="neutral" size="sm">
          {totalQuestions} question{totalQuestions === 1 ? '' : 's'}
        </Pill>
        <Pill tone="neutral" size="sm">
          {!graded
            ? 'Your answers are recorded'
            : retryMode === 'try_again'
              ? 'Retry each question until correct'
              : 'Marked at the end'}
        </Pill>
      </div>

      {attempts.length > 0 ? (
        <div className="rounded-token border border-line/10 bg-surface-2 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-fg-muted">
            <History className="h-4 w-4" />
            Previous attempts{graded && bestScore != null ? ` · best ${bestScore}%` : ''}
          </div>
          <ul className="space-y-1 text-sm">
            {attempts.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2">
                <span className="text-fg-muted">Attempt {a.attempt_number}</span>
                <span className="flex items-center gap-2">
                  {graded ? (
                    <span className="tabular-nums text-fg-dim">{Math.round(a.score)}%</span>
                  ) : null}
                  <Pill tone={a.passed ? 'success' : 'warning'} size="sm">
                    {!graded ? 'Submitted' : a.passed ? 'Passed' : 'Not passed'}
                  </Pill>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {resume ? (
        <div className="rounded-token border border-warning-soft/30 bg-warning-soft/15 p-3 text-sm text-warning-soft-fg">
          You have a saved attempt in progress — {resume.answered} of {totalQuestions} question
          {totalQuestions === 1 ? '' : 's'} answered
          {resume.updatedAt ? `, saved ${formatSaved(resume.updatedAt)}` : ''}. Resuming picks up
          where you left off.
        </div>
      ) : null}

      <div className="flex items-center gap-2 border-t border-line/10 pt-4">
        <Button
          variant={resume ? 'warning' : 'success'}
          iconLeft={resume ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          onClick={onBegin}
        >
          {resume ? 'Resume' : 'Start'}
        </Button>
        {!resume && attempts.length > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-fg-muted">
            <GraduationCap className="h-4 w-4" />
            Retaking from the start
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Friendly relative-ish stamp for the saved time; falls back to a date. */
function formatSaved(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}
